/* eslint-disable react/no-unstable-nested-components */
import React, { forwardRef } from 'react';

import { Box, Grid } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { RefableHistory } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/History/History';
import useOKRConfig from 'components/hooks/useOKRConfig';
import { useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { themeColors } from 'themes/clients/baseTheme';

import {
  Activities,
  Details,
  InitiativesPanel,
  Alignment,
  CascadedListView,
  CheckInThick,
} from 'assets/assetsV1/iconComponents';

import { SidePanelButton } from '../Components/SidePanelButton';
import ScrollableSectionNavV2 from '../ScrollableSectionNav';
import { useUpdateKRMutation } from '../datahooks';
import { useKRPanelParamsMatch } from '../useKRPanelParamsMatch';
import { RefableAlignment } from './Sections/Alignment/Alignment';
import { RefableCheckIn } from './Sections/CheckIn/CheckIn';
import { RefableDetails } from './Sections/Details/Details';
import { RefableOKRInitiatives } from './Sections/Initiatives/OKRInitiatives';
import { RefableSupportedBy } from './Sections/SupportedBy/SupportedBy';

const GridContainer = styled(Grid)`
  flex: auto 1 1;
  height: 100%;
  grid-template-columns: min-content 1fr;
  grid-template-rows: 1fr min-content;
  grid-template-areas:
    'nav content'
    'comment comment';
  grid-gap: 4px;
`;

const StyledSidePanelButton = styled(SidePanelButton)`
  flex: auto 0 0;
  text-wrap: wrap;
  border-radius: 8px;
  width: 56px;
  height: 56px;
`;

const StyledSidePanelButtonWithSmallFont = ({ ...props }) => (
  <StyledSidePanelButton
    {...props}
    sx={{ '.chakra-text': { fontSize: '0.625rem', lineHeight: '0.75rem', marginTop: '-4px' } }}
  />
);

const SectionContentContainer = styled(Box)`
  padding: 8px 8px;
  width: 100%;
  border-radius: 8px;
  background-color: #ffffff;
`;

const PrimaryPanel = ({ isBulkCheckInScreen }) => {
  const { uuid } = useSelector(kRDrawerSelector);

  const { type } = useSelector(kRDrawerSecondaryPanelSelector);

  const { ids, isOKRPanelOpen, isObjective } = useKRPanelParamsMatch();

  const { mutate, isLoading: isMutating } = useUpdateKRMutation(uuid);

  const isSecondaryPanelOpen = Boolean(type);

  const navContainerStyles = {
    gridArea: 'nav',

    display: 'flex',
    flexDirection: 'column',
    gap: '4px',

    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: '4px',
    overflowY: 'auto',
  };

  const sectionContainerStyles = {
    gridArea: 'content',

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',

    borderRadius: '8px',

    overflowY: 'auto',
    overflowX: 'hidden',
    opacity: isSecondaryPanelOpen ? 0.5 : 1,
    pointerEvents: isSecondaryPanelOpen ? 'none' : 'unset',
  };

  /* 
    Following code is to handle okr config 
    Starts here
  */
  const { IS_KR_CASCADING_ENABLED } = useOKRConfig();

  /* Owners / Collaborators, Objective Level, Visibility only shown for non simple Objective Type */

  const showInitiativeSection = !isObjective;

  const showSupportedBySection = isObjective || (!isObjective && IS_KR_CASCADING_ENABLED);

  /* 
    Above code is to handle okr config 
    Ends here
  */
  const sectionAndNavs = [
    {
      id: 'CheckIn',
      accessFn: () => !isBulkCheckInScreen,
      renderNavItem: ({ selected, onClick }) => (
        <StyledSidePanelButtonWithSmallFont onClick={onClick} selected={selected} Icon={CheckInThick}>
          Check-in
        </StyledSidePanelButtonWithSmallFont>
      ),
      SectionContent: forwardRef((props, ref) => (
        <SectionContentContainer>
          <RefableCheckIn
            mutate={mutate}
            isMutating={isMutating}
            isOKRPanelDisabled={isBulkCheckInScreen}
            ref={ref}
            {...props}
          />
        </SectionContentContainer>
      )),
    },
    {
      id: 'History',
      accessFn: () => true,
      renderNavItem: ({ selected, onClick }) => (
        <StyledSidePanelButtonWithSmallFont onClick={onClick} selected={selected} Icon={Activities}>
          History
        </StyledSidePanelButtonWithSmallFont>
      ),
      SectionContent: forwardRef((props, ref) => (
        <SectionContentContainer>
          <RefableHistory
            ids={ids}
            isOKRPanelOpen={isOKRPanelOpen || isObjective}
            mutate={mutate}
            isMutating={isMutating}
            ref={ref}
            {...props}
          />
        </SectionContentContainer>
      )),
    },
    {
      id: 'Details',
      accessFn: () => true,
      renderNavItem: ({ selected, onClick }) => (
        <StyledSidePanelButtonWithSmallFont
          onClick={onClick}
          selected={selected}
          Icon={(props) => <Details {...props} fill="none" />}
        >
          Details
        </StyledSidePanelButtonWithSmallFont>
      ),
      SectionContent: forwardRef((props, ref) => (
        <SectionContentContainer>
          <RefableDetails
            mutate={mutate}
            isOKRPanelDisabled={isBulkCheckInScreen}
            isMutating={isMutating}
            ref={ref}
            {...props}
          />
        </SectionContentContainer>
      )),
    },
    {
      id: 'Alignment',
      accessFn: () => !isBulkCheckInScreen,
      renderNavItem: ({ selected, onClick }) => (
        <StyledSidePanelButtonWithSmallFont onClick={onClick} selected={selected} Icon={Alignment}>
          {/* Keeping goal name static here to not break the UI */}
          Parent <br />
          Alignment
        </StyledSidePanelButtonWithSmallFont>
      ),
      SectionContent: forwardRef((props, ref) => (
        <SectionContentContainer>
          <RefableAlignment
            mutate={mutate}
            isOKRPanelDisabled={isBulkCheckInScreen}
            isMutating={isMutating}
            ref={ref}
            {...props}
          />
        </SectionContentContainer>
      )),
    },
    {
      id: 'SupportedBy',
      accessFn: () => showSupportedBySection && !isBulkCheckInScreen,
      renderNavItem: ({ selected, onClick }) => (
        <StyledSidePanelButtonWithSmallFont onClick={onClick} selected={selected} Icon={CascadedListView}>
          {/* Keeping goal name static here to not break the UI */}
          Child <br />
          Alignment
        </StyledSidePanelButtonWithSmallFont>
      ),
      SectionContent: forwardRef((props, ref) => (
        <SectionContentContainer>
          <RefableSupportedBy
            mutate={mutate}
            isOKRPanelDisabled={isBulkCheckInScreen}
            isMutating={isMutating}
            ref={ref}
            {...props}
          />
        </SectionContentContainer>
      )),
    },
    {
      id: 'Initiatives',
      accessFn: () => showInitiativeSection && !isBulkCheckInScreen,
      renderNavItem: ({ selected, onClick }) => (
        <StyledSidePanelButtonWithSmallFont onClick={onClick} selected={selected} Icon={InitiativesPanel}>
          Initiatives
        </StyledSidePanelButtonWithSmallFont>
      ),
      SectionContent: forwardRef((props, ref) => (
        <SectionContentContainer>
          <RefableOKRInitiatives
            isOpen={isOKRPanelOpen || isObjective}
            ids={ids}
            buttonText="Add New Initiative"
            headerText="Initiatives"
            mutate={mutate}
            isMutating={isMutating}
            ref={ref}
            isOKRPanelDisabled={isBulkCheckInScreen}
            {...props}
          />
        </SectionContentContainer>
      )),
    },
  ];

  const filteredSectionAndNavs = sectionAndNavs.filter(({ accessFn }) => accessFn());

  return (
    <GridContainer backgroundColor={themeColors.neutral.EBEFF3} p={1}>
      <ScrollableSectionNavV2
        options={filteredSectionAndNavs}
        navContainerStyles={navContainerStyles}
        sectionContainerStyles={sectionContainerStyles}
      />
    </GridContainer>
  );
};

export default PrimaryPanel;
